/**
 * Detects non-actionable "No Listener:" errors thrown by browser extension
 * cross-tab messaging frameworks.
 *
 * Extensions (particularly Safari password managers and tab-sync extensions)
 * dispatch namespaced events like "tabs:outgoing.message.ready" between tabs.
 * When no listener is registered for the event, the framework throws an
 * unhandled `Error: No Listener: <event-name>`. The resulting error has no
 * application stack frames and is not actionable.
 *
 * The same bridge also rejects with a bare `Error: No response` when a
 * request to another tab gets no reply. Seen on Safari with the Immersive
 * Translate extension loaded: the error fired 1ms after the page fetched an
 * asset from s.immersivetranslate.com, with no stack frames at all.
 *
 * Signature:
 * - message starts with "No Listener:", or is exactly "No response"
 * - stack contains no application source frames (typically just the error
 *   message itself, e.g. `"Error: No Listener: tabs:outgoing.message.ready"`)
 */
export function isBrowserExtensionNoListenerNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!message) {
    return false;
  }

  if (!message.startsWith("No Listener:") && message !== "No response") {
    return false;
  }

  if (!stack) {
    return true;
  }

  const frames = stack
    .split("\n")
    .filter((line) => line.trim().startsWith("at "));

  if (frames.length === 0) {
    return true;
  }

  return frames.every((frame) => !_isApplicationSourceFrame(frame));
}

function _isApplicationSourceFrame(frame: string): boolean {
  return (
    frame.includes("/src/") ||
    frame.includes("/app/") ||
    frame.includes("/lib/") ||
    frame.includes("/components/") ||
    frame.includes("webpack://_N_E/./src/")
  );
}
